import React from 'react';
import * as enums from '@local/finsera-core/src/enums';
import { useSelector } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';
import { Constraint, ConstraintDraft } from '@local/finsera-core/src/types/optimizer';

import { defaultConstraints } from './optimizer-utils/optimizer-config';
import { ApiType } from '../../api/api';
import { gibberishGenerator } from '../../helpers';
import { useDebouncedMetadata, useEffectWithoutFirst } from '../../hooks';
import { AssetSelectorSharedLoaderProvider } from '../asset-selector/asset-selector-shared-loader';

export type ProviderProps = {
  children: React.ReactNode;
  dates?: { start: string; end: string };
  definition: types.optimizer.OptimizerConfigDraft;
  excludeAdd?: enums.OPTIMIZER_CONSTRAINT_TYPE_ENUM[];
  hideConstraints?: enums.OPTIMIZER_CONSTRAINT_TYPE_ENUM[];
  isLongOnly: boolean;
  optimizerType: 'basket' | 'backtest';
  readOnly?: boolean;
  riskModelId?: number;
  setDefinition: (_definition: types.optimizer.OptimizerConfigDraft) => void;
  resourceMetadata?: Record<string, any>;
  setResourceMetadata?: (_m: Record<string, any>) => void;
  stacked?: boolean;
  tCostStrategy: string;
  universeId?: number;
  title?: React.ReactNode;
  validationDef: types.common.ValidationErrors;
  customResources?: Partial<types.BaseStore['resources']>;
  assetMasterQueryApi?: ApiType['assetMaster']['assetMaster'];
};

type OptimizerContextTypes = {
  addConstraint: (_constraint: types.optimizer.ConstraintMetadata) => void;
  constraints: types.optimizer.Constraint[];
  sectionsMetadata: Record<string, boolean>;
  setSectionsMetadata: (_v: Record<string, boolean>) => void;
  dates: { start: string; end: string };
  excludeAdd: enums.OPTIMIZER_CONSTRAINT_TYPE_ENUM[];
  handleDisabled: (_constraint: types.optimizer.ConstraintMetadata) => boolean;
  hideConstraints: enums.OPTIMIZER_CONSTRAINT_TYPE_ENUM[];
  isLongOnly: boolean;
  optimizerType: 'basket' | 'backtest';
  readOnly: boolean;
  removeConstraint: (_index: number) => void;
  riskModel: types.riskModel.RiskModel;
  selectedOptimizer: string;
  setConstraints: (_constraints: types.optimizer.Constraint[]) => void;
  setParams: (_index: number, _params: types.optimizer.ConstraintDraft, _callback?: () => void) => void;
  stacked: boolean;
  tCostStrategy: string; // TODO move strategies to enum
  universe: types.universe.Universe;
  isDefaultConstraint: (_constraintKey: string) => boolean;
  validationDef: types.common.ValidationErrors;
  title: React.ReactNode;
  objectiveFunction: types.optimizer.ObjectiveFunctionDraft;
  setObjectiveFunction: (_v: types.optimizer.ObjectiveFunctionDraft) => void;
  optimizerRiskModelId?: number;
  setOptimizerRiskModelId: (_id?: number) => void;
  customResources?: Partial<types.BaseStore['resources']>;
  assetMasterQueryApi?: ApiType['assetMaster']['assetMaster'];
};

const OptimizerContext = React.createContext<OptimizerContextTypes>(null);

const EMPTY_ARRAY: any[] = [];

// This context provider is passed to any component requiring the context
const Provider = ({
  title,
  definition,
  setDefinition,
  readOnly,
  universeId,
  dates,
  isLongOnly,
  resourceMetadata,
  setResourceMetadata,
  children,
  stacked = false,
  tCostStrategy,
  riskModelId,
  optimizerType,
  excludeAdd = EMPTY_ARRAY,
  hideConstraints = EMPTY_ARRAY,
  validationDef,
  customResources,
  assetMasterQueryApi,
}: ProviderProps) => {
  const [constraints, setConstraints] = React.useState<ConstraintDraft[]>(definition?.constraints ?? []);
  const [objectiveFunction, setObjectiveFunction] = React.useState(definition?.objective_function ?? {});
  const [optimizerRiskModelId, setOptimizerRiskModelId] = React.useState(definition?.risk_model_id);
  const [localResourceMetadata, setLocalResourceMetadata] = useDebouncedMetadata(resourceMetadata, setResourceMetadata);

  const [sectionsMetadata, setSectionsMetadata] = React.useState<Record<string, boolean>>(
    localResourceMetadata?.sections || {}
  );

  const storeResources = useSelector((state) => state.resources);
  const resources = customResources ?? storeResources;

  const universe = React.useMemo(
    () => resources?.universes?.find((u) => u.id == universeId) as types.universe.Universe,
    [universeId]
  );

  const riskModel = React.useMemo(
    () => resources?.risk_models?.find((r) => r.id == riskModelId) as types.riskModel.RiskModel,
    [riskModelId]
  );

  useEffectWithoutFirst(() => {
    setDefinition({
      ...definition,
      constraints: constraints as Constraint[],
      objective_function: objectiveFunction,
      risk_model_id: optimizerRiskModelId,
    });
  }, [constraints, objectiveFunction, optimizerRiskModelId]);

  useEffectWithoutFirst(() => {
    setLocalResourceMetadata({ ...localResourceMetadata, sections: sectionsMetadata });
  }, [sectionsMetadata]);

  const setParams = (index: number, params: types.optimizer.ConstraintDraft, callback: () => void) => {
    setConstraints(
      constraints.map((c, i) => {
        if (i === index) return { ...c, ...params };
        return c;
      })
    );
    if (callback) callback();
  };

  const removeConstraint = (index: number) => {
    setConstraints(constraints.filter((_constraint: any, i: number) => index !== i));
  };

  const addConstraint = (constraint: types.optimizer.ConstraintMetadata) => {
    setConstraints([
      ...constraints,
      {
        id: gibberishGenerator.stringGenerator(11),
        constraint_type: constraint.constraint_type,
        ...constraint.defaultParams,
      },
    ]);
  };

  const handleDisabled = (constraint: types.optimizer.ConstraintMetadata): boolean => {
    if (constraint.name == 'Country' && !universeId) return true;
    if (constraint.name == 'Net Exposure' && isLongOnly) return true;
    if (
      constraint.constraint_type == enums.OPTIMIZER_CONSTRAINT_TYPE_ENUM.TCOST_CONSTRAINT &&
      tCostStrategy == 'None'
    ) {
      return true;
    }

    return !!(!constraint.multipleAdd && constraints.find((c) => c.constraint_type == constraint.constraint_type));
  };

  const isDefaultConstraint = (constraintId: string) => {
    const optLongOnlyKey = isLongOnly ? 'long-only' : 'long-short';
    const currDefaultConstraints = defaultConstraints[optimizerType][optLongOnlyKey];

    const currentConstraint = constraints.find((c) => c.id == constraintId);
    if (!currDefaultConstraints.includes(currentConstraint.constraint_type)) return false;

    const findOfType = constraints.find((c) => c.constraint_type == currentConstraint.constraint_type);
    return currentConstraint == findOfType;
  };

  return (
    <OptimizerContext.Provider
      value={{
        addConstraint,
        constraints: constraints as Constraint[],
        sectionsMetadata,
        setSectionsMetadata,
        dates,
        excludeAdd,
        handleDisabled,
        hideConstraints,
        isLongOnly,
        optimizerType,
        readOnly,
        title,
        removeConstraint,
        riskModel,
        selectedOptimizer: definition?.optimizer_type,
        setConstraints,
        setParams,
        stacked,
        tCostStrategy,
        universe,
        isDefaultConstraint,
        validationDef,
        objectiveFunction,
        setObjectiveFunction,
        optimizerRiskModelId,
        setOptimizerRiskModelId,
        customResources,
        assetMasterQueryApi,
      }}
    >
      <AssetSelectorSharedLoaderProvider assetMasterQueryApi={assetMasterQueryApi}>
        {children}
      </AssetSelectorSharedLoaderProvider>
    </OptimizerContext.Provider>
  );
};

const OptimizerContextProvider = Provider;

export { OptimizerContext };
export { OptimizerContextProvider };
